import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { GraduationCap } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const next = sp.get("next");
      if (next) return nav(next);
      nav(u.role === "admin" ? "/admin" : u.role === "instructor" ? "/instructor" : "/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-marino text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1200")', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src="/shield-ompec.svg" alt="OM-PEC" className="h-14 w-auto" />
            <div>
              <span className="font-heading font-black text-2xl block">OM-PEC</span>
              <span className="text-[10px] uppercase tracking-wider text-naranja font-semibold">Protegemos · Fortalecemos</span>
            </div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-heading font-black text-4xl tracking-tight leading-tight">Bienvenido de vuelta</h2>
          <p className="mt-3 text-white/80 max-w-md">Continúa donde lo dejaste. Accede a tus cursos, evaluaciones y certificaciones.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-5">
          <div>
            <h1 className="font-heading font-black text-3xl text-marino tracking-tight">Iniciar sesión</h1>
            <p className="mt-1 text-marino/70 text-sm">Accede a tu panel personalizado.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-marino mb-1">Correo o CURP</label>
            <input
              data-testid="login-email"
              type="text"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tucorreo@ejemplo.com  o  PELJ900101HDFRPS08"
              className="w-full px-4 py-3 border border-gris rounded-lg focus:border-marino focus:outline-none"
            />
            <p className="text-[11px] text-marino/50 mt-1">
              ¿Tomaste un curso presencial? Ingresa con tu CURP y la contraseña temporal <code>om-pec2026</code>.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-marino mb-1">Contraseña</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gris rounded-lg focus:border-marino focus:outline-none"
            />
          </div>

          {err && <div data-testid="login-error" className="text-naranja text-sm bg-naranja/10 border border-naranja/30 rounded-lg p-3">{err}</div>}

          <button
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-naranja text-white py-3 rounded-lg font-semibold hover:bg-naranjaHover transition-colors disabled:opacity-60"
          >
            {loading ? "Ingresando..." : "Ingresar"}
          </button>

          <div className="flex items-center justify-between text-sm">
            <Link to="/forgot-password" data-testid="forgot-link" className="text-marino/70 hover:text-naranja">¿Olvidaste tu contraseña?</Link>
            <Link to="/register" className="text-naranja font-medium">Crear cuenta</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
